import { FormInputProps } from '@/@types/form.types';
import { cn } from '@/lib/utils';
import { Controller } from 'react-hook-form';
import InformationCircle from '@/components/icons/InformationCircle';
import { Button } from '@/components/ui/button';

type Props<T extends {}> = {
	label?: string;
	tags: string[];
	optional?: boolean;
	type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
} & FormInputProps<T>;

const ETagPickerMulti = <T extends {}>({
	label,
	tags,
	optional = false,
	fieldName,
	control,
	className,
	containerClassName,
	disabled,
}: Props<T>) => {
	return (
		<div
			className={cn(
				'w-full flex flex-col gap-1.5 select-none',
				containerClassName,
			)}>
			{label && (
				<h4 className='text-sm font-medium'>
					{label}
					{optional && (
						<span className='text-popover-600'> (optional)</span>
					)}
				</h4>
			)}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange, onBlur },
					fieldState,
					formState,
				}) => {
					const selectedValues = Array.isArray(value) ? (value as number[]) : [];
					
					const toggleValue = (tag: number) => {
						if (selectedValues.includes(tag)) {
							onChange(selectedValues.filter((v) => v !== tag));
						} else {
							onChange([...selectedValues, tag]);
						}
					};

					return (
						<>
							<div className='flex gap-2'>
								{tags.map((tag, index) => (
									<Button
										key={tag}
										variant={
											selectedValues.includes(index)
												? 'default'
												: 'outline'
										}
										className={cn(
											'px-3.5 py-2',
											fieldState.invalid
												? 'border-error-1000'
												: '',
											className,
										)}
										onBlur={onBlur}
										onClick={() => toggleValue(index)}
										disabled={
											disabled || formState.disabled
										}>
										{tag}
									</Button>
								))}
							</div>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</>
					);
				}}
			/>
		</div>
	);
};

export default ETagPickerMulti;
